import React, {useEffect, useRef, useState} from "react";
import "./message.css";
import {
    Send as SendIcon,
    PersonOutline as PersonIcon,
} from "@mui/icons-material";
import {ApiDetails, KEY} from "../../dummyData";
import CircularProgress from "@mui/material/CircularProgress";
import Backdrop from "@mui/material/Backdrop";
import {
    TextField,
    Paper,
    IconButton,
    Typography,
    Avatar,
    Box,
    Tooltip, useMediaQuery, useTheme,
} from "@mui/material";
import CryptoJS from "crypto-js";

export default function MessageModern(props) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md')); // Use 'md' breakpoint for tablet/mobile

    // Decrypt tenant details from localStorage
    let details = CryptoJS.AES.decrypt(
        localStorage.getItem("tenant_details"),
        KEY
    );
    let originalText = details.toString(CryptoJS.enc.Utf8);
    const tenantDetails = new Map(Object.entries(JSON.parse(originalText)));

    // State declarations
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sendingMessage, setSendingMessage] = useState(false);
    const [messageText, setMessageText] = useState("");
    const [error, setError] = useState(null);
    const [landlordName, setLandlordName] = useState("Manager");
    const [lastSeen, setLastSeen] = useState("Online");

    // Refs
    const messageEndRef = useRef(null);
    const inputRef = useRef(null);
    const chatBoxRef = useRef(null);

    // Fetch messages on component mount
    useEffect(() => {
        fetchMessages();

        // Set up auto-refresh for messages (every 15 seconds)
        const intervalId = setInterval(() => {
            fetchMessages(false); // quiet refresh (no loading indicator)
        }, 15000);

        // Clean up interval on component unmount
        return () => clearInterval(intervalId);
    }, []);

    // Scroll to bottom when messages change
    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // Focus input field when component mounts
    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, [loading]);

    // Function to scroll to the bottom of messages
    const scrollToBottom = () => {
        if (messageEndRef.current) {
            messageEndRef.current.scrollIntoView({behavior: "smooth"});
        }
    };

    // Fetch messages from API
    const fetchMessages = (showLoading = true) => {
        if (showLoading) setLoading(true);

        fetch(ApiDetails + "pegasus/visionary/tenant/app/messages/get", {
            method: "POST",
            headers:{
                "token": new Map(
                    Object.entries(JSON.parse(originalText))
                ).get("access_token"),
            },
            body: JSON.stringify({
                id: tenantDetails.get("tenant_id"),
            }),
        })
            .then(async (response) => {
                const data = await response.json();
                if (response.status === 403) {
                    // Session expired - redirect to login
                    localStorage.clear();
                    window.location.href = "/";
                    return;
                }

                if (response.status === 200) {
                    setMessages(data);
                    setError(null);
                } else {
                    setError("Failed to fetch messages");
                    props.snackBar({
                        text: "Failed fetching messages",
                        type: "error",
                    });
                }
                if (showLoading) setLoading(false);
            })
            .catch((err) => {
                console.error(err);
                setError("Failed to fetch messages");
                props.snackBar({
                    text: "Something went wrong. Please try again",
                    type: "error",
                });
                if (showLoading) setLoading(false);
            });
    };

    // Send message to API
    const sendMessage = () => {
        // Validate message
        if (!messageText || messageText.trim() === "") {
            props.snackBar({
                text: "Cannot send an empty message",
                type: "error",
            });
            return;
        }

        setSendingMessage(true);

        // Add optimistic message to UI
        const optimisticMessage = {
            sender_id: tenantDetails.get("tenant_id"),
            message: messageText,
            date_created: new Date().toLocaleString(),
            _optimistic: true // Flag to identify optimistic messages
        };

        setMessages(prevMessages => [...prevMessages, optimisticMessage]);
        setMessageText("");

        // Send message to API
        fetch(ApiDetails + "pegasus/visionary/tenant/app/messages/send", {
            method: "POST",
            headers:{
                "token": new Map(
                    Object.entries(JSON.parse(originalText))
                ).get("access_token"),
            },
            body: JSON.stringify({
                sender_id: tenantDetails.get("tenant_id"),
                recipient_id: tenantDetails.get("landlord_id"),
                message: messageText,
            }),
        })
            .then(async (response) => {
                if (response.status === 403) {
                    // Session expired - redirect to login
                    localStorage.clear();
                    window.location.href = "/";
                    return;
                }

                if (response.status === 200) {
                    // Fetch updated messages
                    fetchMessages(false);
                } else {
                    setError("Failed to send message");
                    props.snackBar({
                        text: "Failed to send message",
                        type: "error",
                    });

                    // Remove the optimistic message if it failed
                    setMessages(prevMessages =>
                        prevMessages.filter(msg => !msg._optimistic)
                    );
                }
                setSendingMessage(false);
            })
            .catch((err) => {
                console.error(err);
                setError("Failed to send message");
                props.snackBar({
                    text: "Something went wrong. Please try again",
                    type: "error",
                });

                // Remove the optimistic message if it failed
                setMessages(prevMessages =>
                    prevMessages.filter(msg => !msg._optimistic)
                );
                setSendingMessage(false);
            });
    };

    // Handle key press for sending messages
    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    // Format message date
    const formatMessageDate = (dateString) => {
        try {
            const msgDate = new Date(dateString);
            const today = new Date();

            // Check if the message was sent today
            if (msgDate.toDateString() === today.toDateString()) {
                return msgDate.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'});
            } else {
                // Check if the message was sent this year
                if (msgDate.getFullYear() === today.getFullYear()) {
                    return msgDate.toLocaleDateString([], {month: 'short', day: 'numeric'}) +
                        ' at ' + msgDate.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'});
                } else {
                    return msgDate.toLocaleDateString([], {year: 'numeric', month: 'short', day: 'numeric'}) +
                        ' at ' + msgDate.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'});
                }
            }
        } catch (e) {
            return dateString; // Fallback to original string if parsing fails
        }
    };

    // Get initials for avatar
    const getInitials = (name) => {
        if (!name) return 'PM';

        const nameParts = name.split(' ');
        if (nameParts.length === 1) return nameParts[0].charAt(0).toUpperCase();

        return (nameParts[0].charAt(0) + nameParts[nameParts.length - 1].charAt(0)).toUpperCase();
    };

    // Loading backdrop
    const loadingBackdrop = (
        <Backdrop
            sx={{color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1}}
            open={loading}
        >
            <CircularProgress color="inherit"/>
        </Backdrop>
    );

    // Render message bubble
    const renderMessage = (message, index) => {
        const isMyMessage = message.sender_id === tenantDetails.get("tenant_id");
        const isOptimistic = message._optimistic;

        return (
            <Box
                key={index}
                sx={{
                    display: 'flex',
                    justifyContent: isMyMessage ? 'flex-end' : 'flex-start',
                    mb: 3,
                    opacity: isOptimistic ? 0.7 : 1,
                }}
            >
                {(!isMobile && !isMyMessage) && (
                    <Avatar
                        sx={{
                            bgcolor: '#3B82F6',
                            width: 42,
                            height: 42,
                            mr: 2,
                            mt: 1,
                            fontSize: '1rem',
                            fontWeight: 600
                        }}
                    >
                        {getInitials(landlordName)}
                    </Avatar>
                )}

                <Box
                    sx={{
                        maxWidth: '75%',
                        bgcolor: isMyMessage ? '#E3F2FD' : 'white',
                        borderRadius: '20px',
                        borderTopRightRadius: isMyMessage ? '6px' : '20px',
                        borderTopLeftRadius: !isMyMessage ? '6px' : '20px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                        p: "10px",
                        position: 'relative',
                        border: isMyMessage ? '1px solid #BBDEFB' : '1px solid #F0F0F0'
                    }}
                >
                    <Typography
                        variant="body1"
                        component="div"
                        sx={{
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            color: isMyMessage ? '#1565C0' : '#424242',
                            fontSize: '15px',
                            lineHeight: 1.5,
                            fontFamily: 'inherit', // Preserves font family
                            textAlign: 'left', // Preserves text alignment
                            margin: 0, // Removes default margins
                            padding: 0, // Removes default padding
                            '& *': {
                                whiteSpace: 'pre-wrap !important'
                            }
                        }}
                        dangerouslySetInnerHTML={{__html: message.message}}
                    />

                    <Typography
                        variant="caption"
                        component="div"
                        sx={{
                            color: '#9E9E9E',
                            mt: 1.5,
                            textAlign: 'right',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            gap: 0.5,
                            fontSize: '11px'
                        }}
                    >
                        {formatMessageDate(message.date_created)}
                        {isOptimistic && (
                            <CircularProgress size={10} sx={{ml: 1}}/>
                        )}
                    </Typography>
                </Box>

                {(!isMobile && isMyMessage) && (
                    <Avatar
                        sx={{
                            bgcolor: '#4CAF50',
                            width: 42,
                            height: 42,
                            ml: 2,
                            mt: 1,
                            fontSize: '1rem',
                            fontWeight: 600
                        }}
                    >
                        {getInitials(tenantDetails.get("first_name") + " " + tenantDetails.get("last_name"))}
                    </Avatar>
                )}
            </Box>
        );
    };

    return (
        <Box
            sx={{
                height: 'calc(100vh - 80px)', // Full height minus topbar
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: '#f9fafb',
                position: 'relative'
            }}
        >
            {loadingBackdrop}

            {/* Chat Header */}
            <Paper
                elevation={1}
                sx={{
                    p: 1,
                    bgcolor: 'white',
                    borderBottom: '1px solid #E5E7EB',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderRadius: 0,
                    zIndex: 1
                }}
            >
                <Box sx={{display: 'flex', alignItems: 'center'}}>
                    <Avatar
                        sx={{
                            bgcolor: '#3B82F6',
                            width: 50,
                            height: 50,
                            fontSize: '1.5rem',
                            fontWeight: 600
                        }}
                    >
                        {getInitials(landlordName)}
                    </Avatar>
                    <Box sx={{ml: 3}}>
                        <Typography
                            variant="h5"
                            component="div"
                            sx={{fontWeight: 700, color: '#1F2937', mb: 0.5}}
                        >
                            {landlordName}
                        </Typography>
                        <Typography
                            variant="body2"
                            component="div"
                            sx={{
                                color: '#10B981',
                                display: 'flex',
                                alignItems: 'center',
                                fontWeight: 500
                            }}
                        >
                            <Box
                                component="span"
                                sx={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: '50%',
                                    bgcolor: '#10B981',
                                    display: 'inline-block',
                                    mr: 1
                                }}
                            />
                            {lastSeen}
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            {/* Messages Area */}
            <Box
                ref={chatBoxRef}
                sx={{
                    // px: { xs: 2, sm: 4 },
                    px: "10px",
                    flex: 1,
                    overflowY: 'auto',
                    bgcolor: '#F8FAFC',
                    backgroundImage: 'linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 100%)',
                    position: 'relative',
                    // Custom scrollbar
                    '&::-webkit-scrollbar': {
                        width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                        background: 'transparent',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        background: '#CBD5E1',
                        borderRadius: '10px',
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                        background: '#94A3B8',
                    },
                }}
            >
                {messages.length === 0 && !loading ? (
                    <Box
                        sx={{
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#6B7280'
                        }}
                    >
                        <PersonIcon sx={{fontSize: 64, mb: 2, color: '#D1D5DB'}}/>
                        <Typography variant="h5" sx={{mb: 2, fontWeight: 600, color: '#374151'}}>
                            No messages yet
                        </Typography>
                        <Typography variant="body1" sx={{textAlign: 'center', maxWidth: 300}}>
                            Start the conversation by sending a message to your property manager
                        </Typography>
                    </Box>
                ) : (
                    <>
                        {messages.map((message, index) => renderMessage(message, index))}
                        <div ref={messageEndRef}/>
                    </>
                )}
            </Box>

            {/* Message Input Area */}
            <Paper
                elevation={1}
                sx={{
                    px: 2,
                    py: 1.5,
                    bgcolor: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    borderRadius: 0,
                    borderTop: '1px solid #E5E7EB',
                    minHeight: 'auto'
                }}
            >
                <Box sx={{flex: 1}}>
                    <TextField
                        inputRef={inputRef}
                        placeholder="Type..."
                        multiline
                        maxRows={3}
                        value={messageText}
                        onChange={(e) => setMessageText(e.target.value)}
                        onKeyDown={handleKeyPress}
                        variant="outlined"
                        fullWidth
                        size="small"
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '20px',
                                backgroundColor: '#F8FAFC',
                                fontSize: '14px',
                                minHeight: '40px',
                                '& fieldset': {
                                    borderColor: '#E5E7EB',
                                },
                                '&:hover fieldset': {
                                    borderColor: '#D1D5DB',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: '#3B82F6',
                                    borderWidth: '1px'
                                },
                            },
                            '& .MuiInputBase-input': {
                                py: 1.5,
                                px: 2
                            },
                            '& .MuiInputBase-root': {
                                alignItems: 'center'
                            }
                        }}
                        /* InputProps={{
                             startAdornment: (
                                 <InputAdornment position="start" sx={{ ml: 0.5 }}>
                                     <Tooltip title="Add emoji">
                                         <IconButton
                                             size="small"
                                             sx={{
                                                 color: '#9CA3AF',
                                                 width: 32,
                                                 height: 32,
                                                 '&:hover': {
                                                     color: '#3B82F6',
                                                     bgcolor: 'rgba(59, 130, 246, 0.08)'
                                                 }
                                             }}
                                         >
                                             <EmojiIcon fontSize="small" />
                                         </IconButton>
                                     </Tooltip>
                                 </InputAdornment>
                             )
                         }}*/
                    />
                </Box>

                <Tooltip title="Send message">
                    <IconButton
                        disabled={sendingMessage || !messageText || messageText.trim() === ""}
                        onClick={sendMessage}
                        sx={{
                            bgcolor: '#3B82F6',
                            color: 'white',
                            width: 44,
                            height: 44,
                            '&:hover': {
                                bgcolor: '#2563EB',
                                transform: 'scale(1.03)'
                            },
                            '&.Mui-disabled': {
                                bgcolor: '#F3F4F6',
                                color: '#D1D5DB',
                            },
                            transition: 'all 0.15s ease-in-out'
                        }}
                    >
                        {sendingMessage ? (
                            <CircularProgress size={20} sx={{color: 'white'}}/>
                        ) : (
                            <SendIcon sx={{fontSize: 20}}/>
                        )}
                    </IconButton>
                </Tooltip>
            </Paper>
        </Box>
    );
}